import { Alert, Button, Skeleton, Switch } from '@mantine/core';
import { IconAlertCircle, IconRefresh, IconShip } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { Panel } from '../../Panel.js';
import type { DrydockSettings } from '../drydock/types.js';
import { CardFooter, settingsClasses } from './SettingsLayout.js';

export interface DrydockSettingsSource {
  load(): Promise<DrydockSettings>;
  /** Yoksa salt okunur ("Ayarlar / düzenleme" yetkisi yok). */
  save?: (rowVersion: number | null, selfReview: boolean) => Promise<DrydockSettings>;
}

export interface DrydockSettingsPanelProps {
  /** `null`: okunuyor ya da okunamadı. */
  settings: DrydockSettings | null;
  error?: boolean;
  onRetry?: () => void;
  onSave?: (rowVersion: number | null, selfReview: boolean) => Promise<void>;
}

/**
 * Ayarlar ▸ Dry dock (ADR 0129 Ek Faz 3; sahip kararı 2026-10-26): "Allow self-review of
 * pre-docking inspection results". Varsayılan kapalı: kişi kendi sonucunu, bulgusunu ya da işini
 * inceleyemez. Açıkken inceleyebilir; inceleme kaydı "self-review" iziyle tutulur. Web ofiste
 * düzenlenir; gemiye senkronla iner.
 */
export function DrydockSettingsPanel({
  settings,
  error,
  onRetry,
  onSave,
}: DrydockSettingsPanelProps) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drydock.settings.${key}`);
  const [value, setValue] = useState(settings?.selfReview ?? false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (settings) setValue(settings.selfReview);
  }, [settings]);
  const body = (() => {
    if (error) {
      return (
        <EmptyState
          tone="danger"
          title={t('common.loadError')}
          action={
            onRetry ? (
              <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={onRetry}>
                {k('retry')}
              </Button>
            ) : undefined
          }
        />
      );
    }
    if (!settings) {
      return (
        <div aria-busy="true">
          <Skeleton height={18} mb={10} />
          <Skeleton height={18} />
        </div>
      );
    }
    return (
      <>
        <p className={settingsClasses.intro}>{k('text')}</p>
        <Switch
          label={k('selfReview')}
          description={k('selfReviewHint')}
          checked={value}
          disabled={!onSave || busy}
          onChange={(event) => {
            setSaved(false);
            setFailure(false);
            setValue(event.currentTarget.checked);
          }}
        />
        {failure ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />} role="alert" mt="sm">
            {k('error')}
          </Alert>
        ) : saved ? (
          <Alert color="green" role="status" mt="sm">
            {k('saved')}
          </Alert>
        ) : null}
        {onSave ? (
          <CardFooter>
            <Button
              loading={busy}
              disabled={value === settings.selfReview}
              onClick={async () => {
                setBusy(true);
                setFailure(false);
                try {
                  await onSave(settings.rowVersion, value);
                  setSaved(true);
                } catch {
                  setFailure(true);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {k('save')}
            </Button>
          </CardFooter>
        ) : null}
      </>
    );
  })();
  return (
    <Panel title={k('title')} icon={<IconShip size={18} stroke={1.8} />} divider>
      {body}
    </Panel>
  );
}

/** Kaynağa bağlı bölüm: okur, kaydeder, kaydedileni gösterir. */
export function DrydockSettingsSection({ source }: { source: DrydockSettingsSource }) {
  const [settings, setSettings] = useState<DrydockSettings | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` yeniden okutur
  useEffect(() => {
    let live = true;
    setError(false);
    source
      .load()
      .then((value) => {
        if (live) setSettings(value);
      })
      .catch(() => {
        if (live) setError(true);
      });
    return () => {
      live = false;
    };
  }, [source, attempt]);
  const save = source.save;
  return (
    <DrydockSettingsPanel
      settings={settings}
      error={error}
      onRetry={() => setAttempt((n) => n + 1)}
      {...(save
        ? {
            onSave: async (rowVersion: number | null, selfReview: boolean) => {
              setSettings(await save(rowVersion, selfReview));
            },
          }
        : {})}
    />
  );
}
